import React, { useState } from 'react';
import DropdownMenu from '../DropdownMenu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Breakpoint, MenuItem, SxProps } from '@mui/material';
import { Modal } from 'framework/ui/ui-builder';
import { useAuthorization } from 'framework/authorization';
import { useShowContext } from 'framework/show/ShowContext';
import { AfterSubmitProps } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { useUiTheme } from 'framework/uiTheme';

export type FormActionType = {
  Component: React.FC<any>;
  title: string;
  afterSubmit?: AfterSubmitProps<(response: FormFieldsData) => void>;
  modalSize?: Breakpoint | false;
};
export type DropdownMenuActionType = {
  form?: FormActionType;
  title: string;
  icon?: React.ReactElement;
  onClick?: (data: any) => void;
  isVisible?: boolean | ((data: any) => boolean);
  permission?: string | string[];
};

type DropdownMenuActionsProps = {
  actions: DropdownMenuActionType[];
  permission?: string | string[];
  isVisible?: (data: any) => boolean;
  iconButton?: React.ReactNode;
  sxList?: SxProps;
  sxPaper?: SxProps;
};

const DropdownMenuActions = ({
  actions,
  permission = '',
  isVisible,
  iconButton,
  sxList,
  sxPaper,
}: DropdownMenuActionsProps) => {
  const { isGranted } = useAuthorization();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<Nullable<FormActionType>>(null);
  const { data } = useShowContext();
  const { Button } = useUiTheme();

  const entityData = data.getData();

  const closeModal = () => {
    setModalOpen(false);
    setModalConfig(null);
  };

  const visibleActions = actions?.filter(item => {
    const { isVisible: itemIsVisible } = item;

    return (
      (typeof itemIsVisible === 'function' ? itemIsVisible(data) : itemIsVisible !== false) &&
      (item.permission ? isGranted(item.permission) : true)
    );
  });

  if ((isVisible && isVisible(entityData)) || (permission && !isGranted(permission))) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        disablePortal={false}
        popupId="document-actions"
        sxList={sxList}
        sxPaper={sxPaper}
        iconButton={
          iconButton ?? (
            <Button link sx={{ fontWeight: '400' }}>
              Actions
              <ExpandMoreIcon sx={{ ml: '3px', fontSize: '15px' }} />
            </Button>
          )
        }
      >
        {visibleActions.map(action => {
          const { title, icon, onClick, form } = action;
          return (
            <MenuItem
              key={title}
              onClick={() => {
                if (onClick) {
                  onClick(entityData);
                } else if (form) {
                  setModalOpen(true);
                  setModalConfig({
                    ...form,
                  });
                }
              }}
            >
              {icon}
              {title}
            </MenuItem>
          );
        })}
      </DropdownMenu>

      {modalConfig && (
        <Modal
          isOpen={modalOpen}
          title={modalConfig.title}
          onToggle={closeModal}
          hasCancelButton
          className="form-modal"
          modalSize={modalConfig.modalSize ?? 'sm'}
          paperSx={{ backgroundColor: 'common.white' }}
        >
          <modalConfig.Component modalOpen={modalOpen} closeModal={closeModal} afterSubmit={modalConfig.afterSubmit} />
        </Modal>
      )}
    </>
  );
};

export default DropdownMenuActions;
